import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  carregarPainelPresencial,
  vagasDaModalidade,
  type PainelPresencial,
} from '@/lib/presencial';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import Card from '@/components/ui/Card';
import FilaPorSenha from '../validar/components/FilaPorSenha';

interface Props {
  eventoId: number;
}

// O painel de inscrição presencial (presencial_painel) e a fila pela senha,
// agora dentro de Campistas. A tela de bipar QR continua na rota de sempre.
export default function PresencialCampistas({ eventoId }: Props) {
  const navigate = useNavigate();
  const [painel, setPainel] = useState<PainelPresencial | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [modalidadeSenhaId, setModalidadeSenhaId] = useState<number | null>(null);

  useEffect(() => {
    let ativo = true;
    (async () => {
      setCarregando(true);
      setErro(null);
      try {
        const p = await carregarPainelPresencial(eventoId);
        if (ativo) setPainel(p);
      } catch (e) {
        if (ativo) setErro(mensagemAmigavel(e, 'Não foi possível carregar a inscrição presencial.'));
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [eventoId]);

  const modalidades = painel?.modalidades ?? [];
  const comSenha = modalidades.filter((m) => m.usa_senha);
  const modalidadeSenha =
    comSenha.find((m) => m.id === modalidadeSenhaId) ?? comSenha[0] ?? null;

  if (carregando) {
    return (
      <div className="space-y-3">
        <div className="h-28 esqueleto rounded-3xl bg-secondary-100" />
        <div className="h-64 esqueleto rounded-3xl bg-secondary-100" />
      </div>
    );
  }

  if (erro) {
    return (
      <Card className="p-10 text-center">
        <p className="text-[15px] text-foreground-800">{erro}</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-4 cursor-pointer whitespace-nowrap rounded-2xl bg-primary-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
        >
          Tentar de novo
        </button>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {modalidades.length > 0 && (
        <Card className="p-4">
          <h2 className="text-[15px] font-bold text-foreground-950">Inscrição presencial</h2>
          <div className="mt-3 space-y-3">
            {modalidades.map((m) => (
              <div key={m.id}>
                <p className="truncate text-[15px] font-semibold text-foreground-900">{m.nome}</p>
                <p className="mt-0.5 text-[13px] text-foreground-600">
                  Com vaga {m.com_vaga}
                  {(() => {
                    const total = vagasDaModalidade(m);
                    return total != null ? `/${total}` : '';
                  })()}{' '}
                  · Lista de espera {m.lista_espera} · Aguardando {m.aguardando}
                </p>
                {m.grupos.length > 0 && (
                  <div className="mt-1 space-y-0.5">
                    {m.grupos.map((g) => (
                      <p key={g.id} className="text-[12px] text-foreground-500">
                        {g.nome}: {g.com_vaga}
                        {g.vagas != null ? `/${g.vagas}` : ''} · {g.aguardando} aguardando
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {comSenha.length > 1 && (
        <div className="-mx-4 overflow-x-auto px-4 md:mx-0 md:px-0">
          <div className="flex gap-2 whitespace-nowrap">
            {comSenha.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setModalidadeSenhaId(m.id)}
                className={`shrink-0 cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  modalidadeSenha?.id === m.id
                    ? 'bg-secondary-500 text-white'
                    : 'border border-secondary-300 bg-white text-foreground-800 hover:bg-background-100'
                }`}
              >
                {m.nome}
              </button>
            ))}
          </div>
        </div>
      )}

      {modalidadeSenha ? (
        <FilaPorSenha
          modalidadeId={modalidadeSenha.id}
          modalidadeNome={modalidadeSenha.nome}
        />
      ) : (
        <Card className="p-8 text-center">
          <p className="text-[15px] text-foreground-700">
            Nenhuma modalidade usa a senha da fila. Use o leitor de QR para dar as vagas.
          </p>
        </Card>
      )}

      <button
        type="button"
        onClick={() => navigate(`/gerenciar/${eventoId}/validar`)}
        className="flex min-h-[52px] w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-primary-500 px-5 text-[15px] font-bold text-white shadow-lg shadow-primary-500/30 transition-colors hover:bg-primary-600"
      >
        <span className="flex h-5 w-5 items-center justify-center">
          <i className="ri-qr-scan-2-line" aria-hidden />
        </span>
        Abrir leitor de QR
      </button>
    </div>
  );
}